<template>
    <div v-if="!!fareTypes && !!fareTypes.length" class="price-specification">
        <div
            v-for="(fareType, index) in fareTypes"
            class="price-section"
            :class="fareTypes.length - 1 !== index && 'noBorder'"
            :key="index">
            <div class="price-section-title">
                {{ fareType.NumberOfPax }}
                {{
                    t({
                        prefix: ' x ',
                        key: `checkout.${getFareType(fareType.FareType)}`,
                    })
                }}
            </div>
            <div class="price-section-rows">
                <PriceSpecificationRow
                    v-if="fareType.FlightPrice"
                    :price="fareType.FlightPrice"
                    :description="t({ key: 'checkout.flight_price' })" />

                <PriceSpecificationRow
                    v-if="fareType.TaxesAndFees"
                    :price="fareType.TaxesAndFees"
                    :description="t({ key: 'checkout.taxes_and_fees' })" />

                <PriceSpecificationRow
                    v-if="fareType.BookingFee"
                    :price="fareType.BookingFee"
                    :description="t({ key: 'checkout.booking_fee' })" />

                <PriceSpecificationRow
                    :price="getTotalPrice(fareType)"
                    class="total"
                    :subPrice="fareType.NumberOfPax > 1 ? fareType.TotalPrice : undefined"
                    :subDescription="t({ key: 'shared.per_person' })"
                    :description="t({ key: `${getTranslation(fareType).plural}` })" />
            </div>
            <Divider color="lightGrey" bottomMargin="xl" topMargin="xxl" thickness="1" />
        </div>
    </div>
</template>

<script lang="ts">
    import { Money } from '@libs/money/money.interfaces';
    import { defineComponent, PropType } from 'vue';
    import {
        FarePassengerType,
        FlightPriceBreakdownByFareType,
    } from '@/api/Cart/cart.interfaces';
    import { t } from '@shared/helpers/translation';
    import PriceSpecificationRow from '../PriceSpecificationRow.vue';
    import Divider from '@ui/components/Divider/Divider.vue';

    interface TranslationString {
        singular: string;
        plural: string;
    }

    export default defineComponent({
        name: 'PriceSpecificationTravellers',
        methods: { t },
        components: {
            Divider,
            PriceSpecificationRow,
        },
        props: {
            fareTypes: {
                type: Array as PropType<FlightPriceBreakdownByFareType[]>,
                required: true,
            },
        },
        setup() {
            function getTranslation(
                priceBreakdownByFareType: FlightPriceBreakdownByFareType
            ): TranslationString {
                switch (priceBreakdownByFareType.FareType) {
                    case 1:
                        return {
                            singular: 'checkout.total_price_per_adult',
                            plural: 'checkout.total_price_for_all_adults',
                        };
                    case 2:
                        return {
                            singular: 'checkout.total_price_per_child',
                            plural: 'checkout.total_price_for_all_children',
                        };
                    case 3:
                        return {
                            singular: 'checkout.total_price_per_infant',
                            plural: 'checkout.total_price_for_all_infants',
                        };
                    case 4:
                        return {
                            singular: 'checkout.total_price_per_youth',
                            plural: 'checkout.total_price_for_all_youths',
                        };
                    default:
                        return {
                            singular: 'checkout.total_price_per_person',
                            plural: 'checkout.total_price_for_all_persons',
                        };
                }
            }

            function getFareType(farePassengerType: FarePassengerType): string {
                switch (farePassengerType) {
                    case 1:
                        return 'adult';
                    case 2:
                        return 'child';
                    case 3:
                        return 'infant';
                    case 4:
                        return 'youth';
                    default:
                        return 'unknown';
                }
            }

            const getTotalPrice = ({
                TotalPrice,
                NumberOfPax,
            }: FlightPriceBreakdownByFareType): Money => ({
                Amount: TotalPrice.Amount * NumberOfPax,
                Currency: TotalPrice.Currency,
            });

            return {
                getTranslation,
                getTotalPrice,
                getFareType,
            };
        },
    });
</script>
<style lang="scss">
    @forward '@/components/shared/PriceSpecification/PriceSpecification';
</style>
